import { useRef, useState } from "react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import { cn } from "~/lib/utils";
import {
  COLUMN_DENSITY_PX,
  ROW_HEIGHT_PX,
  TIMELINE_MIN_WIDTH_PX,
  TimelineGrid,
  clamp,
  formatRange,
  generateColumns,
  parseDate,
  type TimelineScale,
} from "~/components/projects/ProjectsTimelineGrid";
import { TimelineScaleControl } from "~/components/projects/ProjectsTimelineRenderers";
import { activeTasksInOrder, type ProjectTaskViewProps } from "./taskViewTypes";
import {
  ProjectTaskTimelineWorkColumn,
  type TaskTimelineRow,
} from "./ProjectTaskTimelineWorkColumn";

const TASK_TIMELINE_WORK_COLUMN_DEFAULT_PX = 280;
const TASK_TIMELINE_WORK_COLUMN_MIN_PX = 220;
const TASK_TIMELINE_WORK_COLUMN_MAX_PX = 420;

function taskTimelineRows(tasks: readonly CmmdBoardTask[]): {
  readonly datedRows: ReadonlyArray<TaskTimelineRow>;
  readonly undatedRows: ReadonlyArray<TaskTimelineRow>;
} {
  const rows = activeTasksInOrder(tasks).map((task) => ({
    task,
    dueDate: parseDate(task.dueDate),
  }));
  return {
    datedRows: rows.filter((row) => row.dueDate !== null),
    undatedRows: rows.filter((row) => row.dueDate === null),
  };
}

function deriveAxisRange(rows: ReadonlyArray<TaskTimelineRow>): {
  readonly axisStart: Date;
  readonly axisEnd: Date;
} {
  let min: Date | null = null;
  let max: Date | null = null;
  for (const row of rows) {
    if (!row.dueDate) continue;
    if (!min || row.dueDate < min) min = row.dueDate;
    if (!max || row.dueDate > max) max = row.dueDate;
  }

  const now = new Date();
  const rawStart = min ?? new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const rawEnd = max ?? new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());
  const axisStart = new Date(rawStart.getFullYear(), rawStart.getMonth() - 1, 1);
  const axisEndCandidate = new Date(rawEnd.getFullYear(), rawEnd.getMonth() + 2, 1);
  const axisEnd =
    axisEndCandidate.getTime() > axisStart.getTime()
      ? axisEndCandidate
      : new Date(axisStart.getFullYear(), axisStart.getMonth() + 1, 1);

  return { axisStart, axisEnd };
}

function endOfTaskMarker(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

export function ProjectTaskTimelineView({
  tasks,
  threadIndex,
  pendingTaskIds,
  onOpenTask,
}: ProjectTaskViewProps) {
  const [scale, setScale] = useState<TimelineScale>("months");
  const [workColumnWidth, setWorkColumnWidth] = useState(TASK_TIMELINE_WORK_COLUMN_DEFAULT_PX);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { datedRows, undatedRows } = taskTimelineRows(tasks);
  const { axisStart, axisEnd } = deriveAxisRange(datedRows);
  const columns = generateColumns(axisStart, axisEnd, scale);
  const totalMs = axisEnd.getTime() - axisStart.getTime() || 1;
  const toPercent = (date: Date) =>
    ((clamp(date, axisStart, axisEnd).getTime() - axisStart.getTime()) / totalMs) * 100;
  const rowCount = datedRows.length + (undatedRows.length > 0 ? 1 + undatedRows.length : 0);
  const timelineMinWidth = Math.max(
    TIMELINE_MIN_WIDTH_PX,
    workColumnWidth + columns.length * COLUMN_DENSITY_PX[scale],
  );

  const setClampedWorkColumnWidth = (px: number) => {
    setWorkColumnWidth(
      Math.min(TASK_TIMELINE_WORK_COLUMN_MAX_PX, Math.max(TASK_TIMELINE_WORK_COLUMN_MIN_PX, px)),
    );
  };

  const jumpToToday = () => {
    const el = scrollRef.current;
    if (!el) return;
    const chartWidth = el.scrollWidth - workColumnWidth;
    const todayPx = workColumnWidth + (toPercent(new Date()) / 100) * chartWidth;
    el.scrollTo({ left: Math.max(0, todayPx - el.clientWidth / 2), behavior: "smooth" });
  };

  return (
    <div className="relative flex min-h-[360px] flex-col overflow-hidden bg-background">
      {rowCount === 0 ? (
        <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-border m-4 p-8 text-center text-sm text-muted-foreground">
          No tasks match the filters.
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="min-h-0 overflow-auto">
            <div className="relative flex min-h-full" style={{ minWidth: timelineMinWidth }}>
              <ProjectTaskTimelineWorkColumn
                datedRows={datedRows}
                undatedRows={undatedRows}
                threadIndex={threadIndex}
                pendingTaskIds={pendingTaskIds}
                width={workColumnWidth}
                onWidthChange={setClampedWorkColumnWidth}
                onResetWidth={() => setWorkColumnWidth(TASK_TIMELINE_WORK_COLUMN_DEFAULT_PX)}
                onOpenTask={onOpenTask}
              />
              <div className="relative min-w-0 flex-1">
                <TimelineGrid
                  months={columns}
                  axisStart={axisStart}
                  axisEnd={axisEnd}
                  rowCount={rowCount}
                  toPercent={toPercent}
                />
                <div className="relative pt-11">
                  {datedRows.map((row) => (
                    <TaskTimelineBarRow
                      key={row.task.id}
                      row={row}
                      pending={pendingTaskIds.has(row.task.id)}
                      toPercent={toPercent}
                      onOpenTask={onOpenTask}
                    />
                  ))}
                  {undatedRows.length > 0 ? (
                    <div
                      className="relative flex items-center border-b border-border bg-muted px-4 text-[11px] text-muted-foreground"
                      style={{ height: ROW_HEIGHT_PX }}
                    >
                      Add due dates to place these tasks on the timeline.
                    </div>
                  ) : null}
                  {undatedRows.map((row) => (
                    <TaskTimelineUndatedRow
                      key={row.task.id}
                      row={row}
                      pending={pendingTaskIds.has(row.task.id)}
                      onOpenTask={onOpenTask}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <TimelineScaleControl
            scale={scale}
            onScaleChange={setScale}
            onJumpToToday={jumpToToday}
          />
        </>
      )}
    </div>
  );
}

function TaskTimelineBarRow({
  row,
  pending,
  toPercent,
  onOpenTask,
}: {
  readonly row: TaskTimelineRow;
  readonly pending: boolean;
  readonly toPercent: (date: Date) => number;
  readonly onOpenTask: (taskId: number) => void;
}) {
  const dueDate = row.dueDate ?? new Date();
  const leftPct = toPercent(dueDate);
  const rightPct = toPercent(endOfTaskMarker(dueDate));
  const widthPct = Math.max(rightPct - leftPct, 1.2);

  return (
    <div className="relative flex items-center px-0" style={{ height: ROW_HEIGHT_PX }}>
      <button
        type="button"
        onClick={() => onOpenTask(row.task.id)}
        className={cn(
          "absolute h-5 rounded-sm border border-hue-violet-border bg-hue-violet/80 px-2 text-left text-[10px] text-white shadow-[0_0_0_1px_rgba(255,255,255,0.05)_inset] transition-colors hover:bg-hue-violet",
          pending ? "opacity-60" : null,
        )}
        style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: 52 }}
        title={`${row.task.title}: ${formatRange(null, row.dueDate)}`}
      >
        <span className="block truncate">{row.task.title}</span>
      </button>
    </div>
  );
}

function TaskTimelineUndatedRow({
  row,
  pending,
  onOpenTask,
}: {
  readonly row: TaskTimelineRow;
  readonly pending: boolean;
  readonly onOpenTask: (taskId: number) => void;
}) {
  return (
    <div
      className="relative flex items-center gap-1 border-b border-border px-4"
      style={{ height: ROW_HEIGHT_PX }}
    >
      <button
        type="button"
        onClick={() => onOpenTask(row.task.id)}
        className={cn(
          "max-w-72 truncate rounded border border-border bg-muted px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
          pending ? "opacity-60" : null,
        )}
        title={row.task.title}
      >
        {row.task.title}
      </button>
    </div>
  );
}
